import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, ChevronDown, Check, X, ChevronRight, Layers, Pin, PinOff, Search, Star } from 'lucide-react'
import {
  ensureWorkspace,
  restartAppWorkspaceContainer,
  recreateAppWorkspaceContainer,
  rebuildApp,
  getApp,
  listAppSessions,
  listMyApps,
  saveWorkspaceChanges,
  getImportStatus,
  getImportTimeline,
  trackWorkspaceOpen,
  pinApp,
  unpinApp,
  type App,
  type Workspace as WorkspaceInfo,
  type AppSession,
  type ImportStatus,
  type ImportTimelineEvent,
} from '../lib/api'
import { PanelType } from '../components/workspace/PanelToggle'
import WorkspaceLayout from '../components/workspace/WorkspaceLayout'
import MobileWorkspaceLayout from '../components/workspace/MobileWorkspaceLayout'
import WorkspaceContextMenu from '../components/workspace/WorkspaceContextMenu'
import PanelMenu from '../components/workspace/PanelMenu'
import { useIsMobile } from '../hooks/useMediaQuery'
import { useVisualViewport } from '../hooks/useVisualViewport'
import { useWorkspaceFocusHeartbeat } from '../hooks/useWorkspaceFocusHeartbeat'
import HelpWidget from '../components/HelpWidget'
import { useSettings } from '../hooks/useSettings'
import { buildWorkspacePreviewUrl } from '../lib/workspacePreview'

// Available panels constant (docs added conditionally inside component based on appName)

// Storage keys for panel preferences (different for app vs general workspace)
const PANEL_PREFS_KEY = 'workspace-panel-prefs'
const APP_PANEL_PREFS_KEY = 'workspace-app-panel-prefs'

function loadPanelPrefs(hasApp: boolean): PanelType[] {
  const storageKey = hasApp ? APP_PANEL_PREFS_KEY : PANEL_PREFS_KEY
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Validate that parsed value is an array (old format was different)
      if (Array.isArray(parsed)) {
        return parsed
      }
      // Clear old/invalid format
      localStorage.removeItem(storageKey)
    }
  } catch (e) {
    // Clear corrupted data
    localStorage.removeItem(storageKey)
  }
  // Default panels based on context:
  // - App workspace: Preview panel (local iframe browser)
  // - General workspace: Server Browser panel (Xpra)
  return hasApp ? ['preview'] : ['browser']
}

function savePanelPrefs(prefs: PanelType[], hasApp: boolean) {
  const storageKey = hasApp ? APP_PANEL_PREFS_KEY : PANEL_PREFS_KEY
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs))
  } catch (e) {
    // Ignore
  }
}

function formatImportEventTime(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function labelImportStage(stage: string | null | undefined): string {
  switch ((stage || '').toLowerCase()) {
    case 'uploading':
      return 'Upload'
    case 'cloning':
      return 'Clone'
    case 'converting':
      return 'Analysis'
    case 'deploying':
      return 'Deploy'
    case 'verify':
      return 'Verify'
    case 'complete':
      return 'Complete'
    case 'failed':
      return 'Failed'
    default:
      return 'Preparing'
  }
}

// Work area dropdown for switching between sessions in the workspace toolbar.
// "Session" is kept in code/API for backward compatibility (D-03); the user-facing
// icon and strings use the neutral "work area" vocabulary (D-01, ISSUE-U01).
function SessionDropdown({
  appName,
  activeSession,
  onSelectSession,
}: {
  appName: string
  activeSession: string
  onSelectSession: (sessionName: string) => void
}) {
  const [sessions, setSessions] = useState<AppSession[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch sessions on mount and every 30 seconds
  useEffect(() => {
    const load = async () => {
      try {
        const result = await listAppSessions(appName)
        setSessions(result.sessions)
      } catch {
        // Ignore fetch errors silently
      }
    }
    load()
    intervalRef.current = setInterval(load, 30000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [appName])

  const activeSessions = sessions.filter(s => s.status !== 'archived')

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={`Switch work area (current: ${activeSession})`}
        aria-label={`Switch work area, currently ${activeSession}`}
        className="flex items-center gap-1.5 text-sm text-foreground/70 hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-secondary"
      >
        <Layers className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="hidden sm:inline">{activeSession}</span>
        <ChevronDown className="hidden sm:inline w-3 h-3" />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          {/* Right-anchored on the breadcrumb so the panel extends leftward —
              SessionDropdown sits at the right end of the breadcrumb row on
              mobile, and a left-anchored panel was running off the viewport.
              The max-w clamp guards the leftward overflow case as well. */}
          <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[200px] max-w-[min(280px,calc(100vw-1rem))]">
            {activeSessions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No work areas</div>
            ) : (
              activeSessions.map(session => (
                <button
                  key={session.id}
                  onClick={() => {
                    onSelectSession(session.name)
                    setIsOpen(false)
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-secondary transition-colors flex items-center gap-2"
                >
                  <Layers className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{session.name}</span>
                  {session.name === activeSession && (
                    <Check className="w-3.5 h-3.5 text-primary ml-auto flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

// Breadcrumb workspace switcher: turns the app-name segment into a dropdown
// so users can jump to another workspace without going through /apps. Apps
// are loaded lazily (first open) — the user's app list changes rarely and the
// Workspace page is long-lived, so polling would be wasted work.
function WorkspaceSwitcher({
  currentAppName,
  currentDisplayName,
  onNavigateToAppDetails,
}: {
  currentAppName: string
  currentDisplayName: string
  onNavigateToAppDetails: () => void
}) {
  const navigate = useNavigate()
  const [apps, setApps] = useState<App[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [busyPin, setBusyPin] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Reset filters when the dropdown closes so reopening always starts
  // from the natural recency-first view. Defer search focus to the next
  // frame so the input exists in the DOM before we focus it.
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus())
      return
    }
    setSearchQuery('')
    setFavoritesOnly(false)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || apps.length > 0) return
    let cancelled = false
    listMyApps()
      .then((data) => {
        if (!cancelled) setApps(data)
      })
      .catch(() => {
        // Silent — empty list will fall through to the "no other apps" state.
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, apps.length])

  // Exclude the current app, apply user filters (search query +
  // favorites-only toggle), then split into pinned (top, ordered by
  // pinned_rank asc — most recent pin first) and the rest (ordered by
  // recency with alphabetical fallback). The current app is excluded since
  // there's nothing to switch to.
  const { pinnedApps, recentApps } = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const filtered = apps
      .filter((a) => a.name !== currentAppName)
      .filter((a) => (favoritesOnly ? Boolean(a.is_favorited) : true))
      .filter((a) => {
        if (!query) return true
        const display = (a.display_name || '').toLowerCase()
        return display.includes(query) || a.name.toLowerCase().includes(query)
      })
    const pinned = filtered
      .filter((a) => a.is_pinned)
      .sort((a, b) => (a.pinned_rank ?? 0) - (b.pinned_rank ?? 0))
    const rest = filtered
      .filter((a) => !a.is_pinned)
      .sort((a, b) => {
        const at = a.last_visited_at ? Date.parse(a.last_visited_at) : 0
        const bt = b.last_visited_at ? Date.parse(b.last_visited_at) : 0
        if (at !== bt) return bt - at
        return (a.display_name || a.name).localeCompare(b.display_name || b.name)
      })
    return { pinnedApps: pinned, recentApps: rest }
  }, [apps, currentAppName, searchQuery, favoritesOnly])

  const hasFilters = searchQuery.trim().length > 0 || favoritesOnly

  // Optimistic pin toggle: flip state immediately, roll back on API error.
  // Re-pinning bumps an app to pinned_rank=0 and increments every other
  // pinned app's rank to keep ordering stable across the list.
  const togglePin = useCallback(
    async (appName: string, currentlyPinned: boolean) => {
      if (busyPin) return
      setBusyPin(appName)
      const previous = apps
      const next = (() => {
        if (currentlyPinned) {
          return apps.map((a) =>
            a.name === appName ? { ...a, is_pinned: false, pinned_rank: null } : a,
          )
        }
        return apps.map((a) => {
          if (a.name === appName) return { ...a, is_pinned: true, pinned_rank: 0 }
          if (a.is_pinned && a.pinned_rank != null) {
            return { ...a, pinned_rank: a.pinned_rank + 1 }
          }
          return a
        })
      })()
      setApps(next)
      try {
        if (currentlyPinned) {
          await unpinApp(appName)
        } else {
          await pinApp(appName)
        }
      } catch {
        setApps(previous)
      } finally {
        setBusyPin(null)
      }
    },
    [apps, busyPin],
  )

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    // Capture-phase so embedded terminals (xterm.js) don't swallow the key
    // before it reaches us.
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [isOpen])

  const renderAppRow = (app: App) => {
    const pinned = Boolean(app.is_pinned)
    return (
      <div
        key={app.id}
        className="group flex items-center hover:bg-secondary transition-colors"
      >
        <button
          type="button"
          onClick={() => {
            setIsOpen(false)
            navigate(`/workspace?app=${encodeURIComponent(app.name)}`)
          }}
          className="flex-1 min-w-0 px-3 py-2 text-left text-sm flex items-center gap-2"
        >
          <span
            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              app.status === 'running'
                ? 'bg-green-500'
                : app.status === 'error'
                  ? 'bg-red-500'
                  : 'bg-muted-foreground/40'
            }`}
            aria-hidden="true"
          />
          <span className="flex-1 min-w-0 flex flex-col leading-tight">
            <span className="truncate">{app.display_name || app.name}</span>
            <span className="truncate text-[11px] text-muted-foreground">/{app.name}</span>
          </span>
        </button>
        <button
          type="button"
          aria-label={pinned ? `Unpin ${app.display_name || app.name}` : `Pin ${app.display_name || app.name}`}
          title={pinned ? 'Unpin from switcher' : 'Pin to switcher'}
          onClick={(e) => {
            e.stopPropagation()
            void togglePin(app.name, pinned)
          }}
          disabled={busyPin === app.name}
          className={`flex-shrink-0 px-2 py-2 transition-opacity ${
            pinned
              ? 'text-primary opacity-100'
              : 'text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100'
          } disabled:cursor-wait`}
        >
          {pinned ? <Pin className="w-3.5 h-3.5" /> : <PinOff className="w-3.5 h-3.5" />}
        </button>
      </div>
    )
  }

  return (
    <div className="relative min-w-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title={currentDisplayName}
        className="flex w-full items-center gap-1 min-w-0 max-w-[10rem] sm:max-w-[18rem] font-medium text-primary transition-colors hover:underline"
      >
        <span className="truncate">{currentDisplayName}</span>
        <ChevronDown className="w-3 h-3 flex-shrink-0" />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          {/* Fixed width per breakpoint: min-w/max-w let the panel resize as
              items filter, which made it jump horizontally while typing in
              search. A single w- keeps it stable; long names truncate inside
              (renderAppRow has `truncate`). Smaller on mobile because the
              panel is `left-0` anchored to the trigger (which sits ~50px in
              from the viewport edge), so `100vw-2rem` would overflow the
              right edge on small screens. */}
          <div className="absolute left-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-lg w-[min(290px,calc(100vw-2rem))] sm:w-[min(360px,calc(100vw-2rem))] max-h-[min(440px,70vh)] flex flex-col">
            <div className="flex items-center gap-1 px-2 pt-2 pb-2 border-b border-border">
              <div className="relative flex-1 min-w-0">
                <Search
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search apps"
                  aria-label="Search apps"
                  className="w-full pl-7 pr-2 py-1.5 text-sm bg-secondary rounded border border-transparent focus:border-primary focus:bg-card focus:outline-none placeholder:text-muted-foreground"
                />
              </div>
              <button
                type="button"
                aria-label={favoritesOnly ? 'Show all apps' : 'Show only favorites'}
                title={favoritesOnly ? 'Show all apps' : 'Show only favorites'}
                aria-pressed={favoritesOnly}
                onClick={() => setFavoritesOnly((v) => !v)}
                className={`flex-shrink-0 p-1.5 rounded transition-colors ${
                  favoritesOnly
                    ? 'text-yellow-500 hover:text-yellow-600'
                    : 'text-muted-foreground/60 hover:text-yellow-500'
                }`}
              >
                <Star className={`w-4 h-4 ${favoritesOnly ? 'fill-current' : ''}`} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
            {pinnedApps.length === 0 && recentApps.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {hasFilters ? 'No matches' : 'No other apps yet'}
              </div>
            ) : (
              <>
                {pinnedApps.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Pinned
                    </div>
                    {pinnedApps.map(renderAppRow)}
                  </>
                )}
                {recentApps.length > 0 && (
                  <>
                    {pinnedApps.length > 0 && <div className="border-t border-border my-1" />}
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {pinnedApps.length > 0 ? 'Recent' : 'Switch to'}
                    </div>
                    {recentApps.map(renderAppRow)}
                  </>
                )}
              </>
            )}
            </div>
            <div className="border-t border-border" />
            <button
              type="button"
              onClick={() => {
                setIsOpen(false)
                onNavigateToAppDetails()
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-secondary transition-colors text-muted-foreground"
            >
              App details
            </button>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false)
                navigate('/apps')
              }}
              className="w-full px-3 py-2 text-left text-sm hover:bg-secondary transition-colors text-muted-foreground"
            >
              View all apps
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default function Workspace() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const visualViewport = useVisualViewport()
  const { settings, updateSettings: updateSettingsBatch } = useSettings()

  // Get folder parameter for initial working directory
  const folder = searchParams.get('folder')
  // Get app URL for browser panel
  const appUrl = searchParams.get('url')
  // Get app name (either explicit param or extracted from folder path)
  const appNameParam = searchParams.get('app')
  // Extract app name from folder path like /home/coder/workspace/apps/{name}
  const extractedAppName = folder?.match(/(?:\/home\/coder\/)?workspace\/apps\/([^/]+)/)?.[1]
  const appName = appNameParam || extractedAppName
  // Server-side claude_* notification suppression while this tab is focused.
  // See services/router/shared/workspace_focus.py for the why.
  useWorkspaceFocusHeartbeat(appName)

  // Audit "workspace_open" once per (user, app, minute). Without this the
  // activity feed had no signal that someone actually opened the IDE for
  // an app — workspace_session_create only fires on new session worktrees.
  useEffect(() => {
    if (!appName) return
    trackWorkspaceOpen(appName).catch(() => {})
  }, [appName])
  // Optional import id for showing background import progress after redirect
  const importId = useMemo(() => {
    const raw = searchParams.get('import_id')
    if (!raw) return null
    const parsed = Number(raw)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }, [searchParams])
  // Get session parameter (contains terminal URL with tmux session arg)
  // e.g., "/workspace/app/myapp/terminal/?arg=main"
  const sessionParam = searchParams.get('session')
  // Extract the tmux session name from the session URL's arg parameter
  const tmuxSession = sessionParam ? new URLSearchParams(sessionParam.split('?')[1] || '').get('arg') : undefined

  // Session state: activeSession tracks the currently displayed session,
  // requestedSession is the "intent" that TerminalTabs consumes to switch tabs
  const [activeSession, setActiveSession] = useState(tmuxSession || 'main')
  const [requestedSession, setRequestedSession] = useState<string | undefined>(undefined)

  // Handle session selection from dropdown
  const handleSelectSession = useCallback((sessionName: string) => {
    setRequestedSession(sessionName)
    setActiveSession(sessionName)
  }, [])

  // When TerminalTabs acknowledges a session change via onSessionChange callback
  const handleSessionChange = useCallback((sessionName: string) => {
    setActiveSession(sessionName)
  }, [])

  // Workspace state
  const [workspaceStatus, setWorkspaceStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [, setWorkspace] = useState<WorkspaceInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importProgress, setImportProgress] = useState<ImportStatus | null>(null)
  const [importEvents, setImportEvents] = useState<ImportTimelineEvent[]>([])
  const [dismissedImportBannerId, setDismissedImportBannerId] = useState<number | null>(null)
  const [restarting, setRestarting] = useState(false)
  const [recreating, setRecreating] = useState(false)
  const [rebuilding, setRebuilding] = useState(false)
  const [app, setApp] = useState<App | null>(null)
  // Tracks the most recent deploy/rebuild kickoff. When the user (or another
  // tab) triggers Rebuild App, the iframe is still showing the previous
  // container's HTML; without this cache-bust it stays stale until the user
  // manually refreshes. The Workspace polls app status (see below) and bumps
  // this whenever deployment_phase transitions into a non-running state, so
  // the iframe reloads against the proxy and picks up the platform's
  // "App is Building" sentinel.
  const [rebuildSentinel, setRebuildSentinel] = useState<string | null>(null)
  const appTitle = app?.display_name || app?.name || null
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [workspaceNotice, setWorkspaceNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)

  // Compute effective preview URL based on active session:
  // - main session: use dev route (/a/dev/{appName}/) so preview always shows
  //   the live dev container, not the frozen production build
  // - non-main sessions: use branch preview URL (/a/branch/{sessionName}/{appName}/)
  //
  // Fall back to app.url when ?url= query param is absent: in-app navigation
  // paths (breadcrumb workspace switcher, notification deep-links, rename
  // redirect) pass only ?app=NAME, so without this fallback the preview iframe
  // has no source and stays blank until the user reloads through a /apps link.
  const previewSourceUrl = appUrl || app?.url || null
  const effectivePreviewUrl = useMemo(() => {
    if (!previewSourceUrl) return undefined

    const resolvedUrl = buildWorkspacePreviewUrl(
      previewSourceUrl,
      appName,
      activeSession,
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    ) || previewSourceUrl

    // Force a one-time iframe reload when import reaches complete so preview
    // switches off the temporary "Importing..." page without manual refresh.
    if (importId && importProgress?.status === 'complete') {
      try {
        const parsed = new URL(resolvedUrl)
        parsed.searchParams.set('cc_import_complete', importProgress.updated_at || String(importId))
        return parsed.toString()
      } catch {
        return resolvedUrl
      }
    }

    // Same idea for Rebuild App: when the deployment phase moves into a
    // building / starting state we bump `cc_rebuild_at`, forcing the iframe
    // off its cached previous-container HTML so the platform's
    // app_starting_html_response sentinel is rendered instead of a blank or
    // stale page during the rebuild window.
    if (rebuildSentinel) {
      try {
        const parsed = new URL(resolvedUrl)
        parsed.searchParams.set('cc_rebuild_at', rebuildSentinel)
        return parsed.toString()
      } catch {
        return resolvedUrl
      }
    }

    return resolvedUrl
  }, [
    previewSourceUrl,
    appName,
    activeSession,
    importId,
    importProgress?.status,
    importProgress?.updated_at,
    rebuildSentinel,
  ])

  // Panel preferences (context-aware based on app vs general workspace)
  const hasApp = Boolean(appName)
  const [panelPrefs, setPanelPrefs] = useState<PanelType[]>(() => loadPanelPrefs(hasApp))
  const activePanels = useMemo(() => new Set(panelPrefs), [panelPrefs])
  const availablePanels = useMemo(() => {
    const panels: PanelType[] = ['preview', 'browser', 'files']
    if (appName) {
      panels.push('docs')
      panels.push('git')
      panels.push('secrets')
    }
    return panels
  }, [appName])

  // Ensure workspace is ready and user has edit permission
  useEffect(() => {
    let mounted = true

    async function checkWorkspace() {
      try {
        setWorkspaceStatus('loading')

        // Check if user has edit permission on the app
        if (appName) {
          try {
            const fetched = await getApp(appName)
            if (mounted) {
              setApp(fetched)
            }
            const myPermission = fetched.my_permission ?? 'owner'
            const canEdit = ['owner', 'admin', 'edit'].includes(myPermission)

            if (!canEdit) {
              // View-only users should be redirected to the app URL
              if (fetched.url) {
                window.location.href = fetched.url
                return
              }
              // If no URL available, navigate to apps page
              navigate('/apps')
              return
            }
          } catch (err) {
            // If we can't fetch the app, likely unauthorized
            console.error('Failed to verify app permission:', err)
            if (mounted) {
              setError('You do not have permission to access this workspace')
              setWorkspaceStatus('error')
            }
            return
          }
        }

        const ws = await ensureWorkspace()
        if (mounted) {
          if (!appName) {
            setApp(null)
          }
          setWorkspace(ws)
          setWorkspaceStatus('ready')
        }
      } catch (err) {
        // Nested imported platforms may not expose the global /api/workspace
        // bootstrap endpoint, but app-scoped workspace routes can still work.
        if (appName && err instanceof Error && err.message.toLowerCase().includes('not found')) {
          if (mounted) {
            setWorkspace(null)
            setWorkspaceStatus('ready')
          }
          return
        }
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize workspace')
          setWorkspaceStatus('error')
        }
      }
    }

    checkWorkspace()

    return () => {
      mounted = false
    }
  }, [appName, navigate])

  // Track workspace visit for onboarding checklist
  useEffect(() => {
    if (settings?.dashboard?.checklist_step_workspace !== true) {
      updateSettingsBatch({
        dashboard: { ...settings?.dashboard, checklist_step_workspace: true },
      }).catch(() => {
        // Non-critical
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Poll import status (when workspace is opened from import flow)
  useEffect(() => {
    if (!importId) {
      setImportProgress(null)
      setImportEvents([])
      return
    }

    let cancelled = false
    let interval: ReturnType<typeof setInterval> | null = null
    setImportEvents([])

    const poll = async () => {
      try {
        const status = await getImportStatus(importId)
        let timelineEvents: ImportTimelineEvent[] | null = null
        try {
          const timeline = await getImportTimeline(importId, 40)
          timelineEvents = timeline.events
        } catch {
          // Keep polling resilient and preserve the last good timeline
          // instead of resetting the banner back to generic text.
        }
        if (cancelled) return
        setImportProgress(status)
        if (timelineEvents !== null) {
          setImportEvents(timelineEvents)
        }
        if (status.status === 'complete' || status.status === 'failed') {
          if (interval) {
            clearInterval(interval)
            interval = null
          }
        }
      } catch {
        // Keep polling on transient fetch errors
      }
    }

    void poll()
    interval = setInterval(() => {
      void poll()
    }, 2000)

    return () => {
      cancelled = true
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [importId])

  // Poll app status to detect rebuild events and force-reload the preview
  // iframe so the platform's "App is Building" sentinel renders during the
  // rebuild window. Without this the iframe keeps showing the previous
  // container's HTML (or goes blank if it managed a disconnect) until the
  // user manually refreshes. We only poll when there's no in-flight import
  // already driving its own polling, and we throttle to every 5s.
  const lastDeploymentPhaseRef = useRef<string | null>(null)
  useEffect(() => {
    if (!appName) return
    if (importId) return  // import polling already fetches richer state

    let cancelled = false
    const REBUILDING_PHASES = new Set([
      'queued', 'building', 'installing', 'starting',
    ])

    const poll = async () => {
      try {
        const fetched = await getApp(appName)
        if (cancelled) return
        setApp(fetched)
        const phase = (fetched.deployment_phase || '').toLowerCase()
        const prev = lastDeploymentPhaseRef.current
        if (prev && !REBUILDING_PHASES.has(prev) && REBUILDING_PHASES.has(phase)) {
          // Transition into a rebuild window -- bump the cache-bust so the
          // preview iframe reloads and lands on the sentinel.
          setRebuildSentinel(new Date().toISOString())
        }
        lastDeploymentPhaseRef.current = phase
      } catch {
        // Keep polling on transient errors -- nothing to do.
      }
    }

    void poll()
    const interval = setInterval(() => {
      void poll()
    }, 5000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [appName, importId])

  const importBanner = useMemo(() => {
    if (!importId || !importProgress) return null
    if (dismissedImportBannerId === importId) return null

    const status = importProgress.status
    const isFailed = status === 'failed'
    const isComplete = status === 'complete'
    const isInProgress = !isFailed && !isComplete

    // When the import fails BUT the workspace transform agent was seeded,
    // the user has a path forward (agent is already diagnosing). Treat
    // that as "warning, not catastrophe": title is honest about failure,
    // the recovery is highlighted, and the banner is amber rather than
    // destructive-red.
    const stageEvents = importEvents.filter(event => Boolean(event.message))
    const agentSeeded = isFailed && stageEvents.some(e => Boolean(
      (e.details as { agent_session_seeded?: boolean } | undefined)?.agent_session_seeded
    ))

    let title = 'Import in progress'
    let detail = 'Preparing workspace and project files'
    const latestEvent = stageEvents[stageEvents.length - 1] || null
    const recentEvents = stageEvents.slice(-4)
    const latestStageLabel = labelImportStage(latestEvent?.stage || status)
    const latestMessage = latestEvent?.message?.trim()
    if (status === 'cloning') {
      title = 'Cloning repository'
      detail = 'Fetching source code into the workspace'
    } else if (status === 'converting') {
      title = 'Analyzing repository'
      detail = 'Determining how to run the project on the platform'
    } else if (status === 'deploying') {
      title = 'Deploying app'
      detail = 'Building and verifying app startup'
    } else if (status === 'uploading') {
      title = 'Uploading project'
      detail = 'Receiving archive and extracting files'
    } else if (isComplete) {
      title = 'Import complete'
      detail = 'App is ready. You can continue from this workspace.'
    } else if (isFailed && agentSeeded) {
      title = 'Initial deploy failed — workspace agent is diagnosing'
      detail = 'Open the workspace terminal / Claude panel: the codebase-transform skill is already running with the failure context.'
    } else if (isFailed) {
      title = 'Import failed'
      detail = importProgress.error_message || 'Open logs in terminal and retry conversion or deployment.'
    }

    if (latestMessage && !isFailed) {
      detail = latestMessage
    }

    const containerClass = isFailed && agentSeeded
      ? 'border-amber-500/30 bg-amber-500/10'
      : isFailed
        ? 'border-destructive/20 bg-destructive/10'
        : isComplete
          ? 'border-green-500/20 bg-green-500/10'
          : 'border-primary/20 bg-primary/5'
    const titleClass = isFailed && agentSeeded
      ? 'text-amber-700 dark:text-amber-400'
      : isFailed
        ? 'text-destructive'
        : isComplete
          ? 'text-green-700 dark:text-green-400'
          : 'text-foreground'

    return (
      <div className={`px-4 py-2 border-b ${containerClass}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isInProgress && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              <p className={`text-sm font-medium ${titleClass}`}>{title}</p>
            </div>
            <p className="text-xs text-muted-foreground">{detail}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
              <span className="rounded-full border border-border/70 px-2 py-0.5">
                Stage: {latestStageLabel}
              </span>
              {latestEvent?.attempt_no ? (
                <span className="rounded-full border border-border/70 px-2 py-0.5">
                  Attempt {latestEvent.attempt_no}
                </span>
              ) : null}
            </div>
            {recentEvents.length > 0 ? (
              <div className="mt-2 space-y-1">
                {recentEvents.map((event) => (
                  <div key={event.id} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                    <span className="font-mono text-[10px] text-muted-foreground/80">
                      {formatImportEventTime(event.created_at)}
                    </span>
                    <span className="min-w-0 break-words">{event.message}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          
            <button title="Dismiss this notification"
              type="button"
              className="mt-0.5 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors"
              onClick={() => setDismissedImportBannerId(importId)}
              aria-label="Dismiss import status banner"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          
        </div>
      </div>
    )
  }, [dismissedImportBannerId, importEvents, importId, importProgress])

  // Handle panel toggle
  const handlePanelToggle = useCallback((panel: PanelType) => {
    setPanelPrefs((prev) => {
      const isActive = prev.includes(panel)
      const newPanels = isActive
        ? prev.filter((p) => p !== panel)
        : [...prev, panel]
      savePanelPrefs(newPanels, hasApp)
      return newPanels
    })
  }, [hasApp])

  // Handle panel close (from within the panel)
  const handlePanelClose = useCallback((panel: PanelType) => {
    handlePanelToggle(panel)
  }, [handlePanelToggle])

  // Handle container restart
  const handleRestart = useCallback(async () => {
    if (!appName) return

    setRestarting(true)
    try {
      const result = await restartAppWorkspaceContainer(appName)
      if (result.status === 'restarted') {
        // Reload the page to reconnect to the restarted container
        window.location.reload()
        return
      }

      // For cooldown/in-progress no-op responses, keep current page/session.
      setRestarting(false)
    } catch (err) {
      console.error('Failed to restart container:', err)
      setRestarting(false)
    }
  }, [appName])

  const handleRecreate = useCallback(async () => {
    if (!appName) return

    setRecreating(true)
    try {
      // Delete only the container (preserves filesystem); the next page load
      // triggers get_or_create which re-pulls the latest image and re-injects
      // platform env vars. Use when a plain restart isn't enough (eg. updated
      // workspace image or new env vars from a Router upgrade).
      await recreateAppWorkspaceContainer(appName)
      window.location.reload()
    } catch (err) {
      console.error('Failed to recreate container:', err)
      setRecreating(false)
    }
  }, [appName])

  // Rebuild the published app (NOT the workspace dev container) — same action
  // as Rebuild on AppDetail. Deploys current main/ to all users, so it's gated
  // by a confirm dialog inside the toolbar context menu.
  const handleRebuild = useCallback(async () => {
    if (!appName) return
    setRebuilding(true)
    setWorkspaceNotice(null)
    try {
      await rebuildApp(appName)
      setWorkspaceNotice({
        tone: 'success',
        text: 'Rebuild started. Watch progress on the App detail page.',
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Rebuild failed.'
      setWorkspaceNotice({ tone: 'error', text: message })
    } finally {
      setRebuilding(false)
    }
  }, [appName])

  const handleSaveCurrentWork = useCallback(async () => {
    if (!appName || saving) return
    const message = saveMessage.trim()
    if (!message) {
      setWorkspaceNotice({ tone: 'error', text: 'Add a short save message first.' })
      return
    }

    setSaving(true)
    setWorkspaceNotice(null)
    try {
      const result = await saveWorkspaceChanges(appName, {
        session_name: activeSession,
        message,
      })
      setSaveDialogOpen(false)
      setSaveMessage('')
      setWorkspaceNotice({
        tone: 'success',
        text: result.saved
          ? `Saved ${result.changed_file_count} ${result.changed_file_count === 1 ? 'file' : 'files'} in ${result.session_name}.`
          : `Nothing to save in ${result.session_name}.`,
      })
    } catch (err) {
      setWorkspaceNotice({
        tone: 'error',
        text: err instanceof Error ? err.message : 'Failed to save changes',
      })
    } finally {
      setSaving(false)
    }
  }, [activeSession, appName, saveMessage, saving])

  // Loading state
  if (workspaceStatus === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Starting workspace...</p>
      </div>
    )
  }

  // Error state
  if (workspaceStatus === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <p className="text-destructive mb-4">{error || 'Failed to start workspace'}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div
      // h-dvh sizes to the visible viewport on mobile; h-screen (= 100vh)
      // on iOS Safari stays at the device's full screen including the URL-
      // bar area, which would make the body scrollable and let inner-panel
      // scroll gestures drag the whole layout up.
      className="flex flex-col h-dvh bg-background"
      style={
        // dvh doesn't account for the iOS keyboard overlay (keyboard isn't
        // browser chrome), so clamp to visualViewport.height while open —
        // otherwise the live prompt + MobileKeyboardBar would sit behind
        // the keyboard.
        isMobile && visualViewport.isKeyboardOpen
          ? { height: visualViewport.height }
          : undefined
      }
    >
      {/* Toolbar */}
      <div className="sticky top-0 z-40 flex items-center justify-between gap-2 px-4 pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))] border-b border-border bg-card flex-shrink-0">
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <button title={appName ? 'Back to app details' : 'Return to the home page'}
            onClick={() => navigate(appName ? `/apps/${encodeURIComponent(appName)}` : '/')}
            className="p-1.5 rounded hover:bg-secondary transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            {/* "Apps" + first chevron — desktop only; too much chrome on mobile.
                Routes to /apps (My Apps) so the breadcrumb's "Apps" segment
                takes you to the apps list, matching the Apps nav button. */}
            <button
              type="button"
              onClick={() => navigate('/apps')}
              className="hidden sm:inline font-medium text-muted-foreground transition-colors hover:text-foreground hover:underline"
            >
              Apps
            </button>
            {appName && (
              <>
                <ChevronRight className="hidden sm:inline h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <WorkspaceSwitcher
                  currentAppName={appName}
                  currentDisplayName={appTitle || appName}
                  onNavigateToAppDetails={() =>
                    navigate(`/apps/${encodeURIComponent(appName)}`)
                  }
                />
              </>
            )}
            {appName && (
              <>
                {/* Session dropdown sits directly after the app name — the
                    "Workspace" label between them was redundant since the
                    session dropdown already speaks for itself. */}
                <ChevronRight className="hidden sm:inline h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <SessionDropdown
                  appName={appName}
                  activeSession={activeSession}
                  onSelectSession={handleSelectSession}
                />
              </>
            )}
          </div>

        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Two labeled dropdown triggers — Actions first, Panels second. */}
          {app && (
            <WorkspaceContextMenu
              app={app}
              onSaveWork={() => setSaveDialogOpen(true)}
              onRestartWorkspace={handleRestart}
              onRecreateWorkspace={handleRecreate}
              onRebuildApp={handleRebuild}
              saving={saving}
              restarting={restarting}
              recreating={recreating}
              rebuilding={rebuilding}
              onAppUpdated={setApp}
            />
          )}
          <PanelMenu
            availablePanels={availablePanels}
            activePanels={activePanels}
            onToggle={handlePanelToggle}
          />
        </div>
      </div>

      {importBanner}
      {workspaceNotice && (
        <div
          className={`border-b px-4 py-2 text-sm ${
            workspaceNotice.tone === 'success'
              ? 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-300'
              : 'border-destructive/30 bg-destructive/10 text-destructive'
          }`}
        >
          {workspaceNotice.text}
        </div>
      )}

      {/* Workspace content */}
      <div className="flex-1 overflow-hidden">
        {isMobile ? (
          <MobileWorkspaceLayout
            activePanels={activePanels}
            onPanelClose={handlePanelClose}
            cwd={folder || undefined}
            browserUrl={effectivePreviewUrl || undefined}
            appName={appName || undefined}
            tmuxSession={tmuxSession || undefined}
            requestedSession={requestedSession}
            onSessionChange={handleSessionChange}
          />
        ) : (
          <WorkspaceLayout
            activePanels={activePanels}
            onPanelClose={handlePanelClose}
            onRequestSession={handleSelectSession}
            cwd={folder || undefined}
            browserUrl={effectivePreviewUrl || undefined}
            appName={appName || undefined}
            tmuxSession={tmuxSession || undefined}
            requestedSession={requestedSession}
            onSessionChange={handleSessionChange}
          />
        )}
      </div>
      {saveDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              void handleSaveCurrentWork()
            }}
            className="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-lg"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Save work</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Add a short note for this saved version in {activeSession}.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!saving) setSaveDialogOpen(false)
                }}
                disabled={saving}
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-50"
                aria-label="Close save dialog"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <label className="mt-4 block text-sm font-medium" htmlFor="workspace-save-message">
              What changed?
            </label>
            <input
              id="workspace-save-message"
              value={saveMessage}
              onChange={(event) => setSaveMessage(event.target.value)}
              disabled={saving}
              autoFocus
              maxLength={200}
              placeholder="Example: Update checkout form copy"
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-primary disabled:opacity-50"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSaveDialogOpen(false)}
                disabled={saving}
                className="rounded-lg border border-border px-4 py-2 text-sm transition-colors hover:bg-secondary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !saveMessage.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </div>
          </form>
        </div>
      )}
      {!isMobile && settings?.dashboard?.help_widget_enabled !== false && <HelpWidget />}
    </div>
  )
}
